import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { View, Platform } from 'react-native'
import styled from 'styled-components/native'
import { BlurView } from 'expo-blur'
import moment from 'moment-timezone'
import Svg, { G, Path } from 'react-native-svg'
import visitor from '../../services/visitor'
import SmartImage from '../SmartImage'
import Avatar from './message/Avatar'
import Question from './message/Question'
import File from './message/File'
import Poll from './message/Poll'
import { WebinarContext } from '../Webinar/Context'
import { AttendeeAvatar, AvatarInitial } from '../Webinar/Attendees'

const mobile = Platform.OS === 'ios' || Platform.OS === 'android'

const Message = props => {
  const { cid, darkScheme } = useContext(WebinarContext)
  const { id, author, answeredBy, appearIn, data, type, ctaId } = props
  const timeZone = moment.tz.guess()
    const duration = moment.duration(appearIn)
  let human_created_at
  if (visitor.viewer_session.scheduled_at) {
    human_created_at = moment(visitor.viewer_session.scheduled_at).add(duration).tz(timeZone).format('LT')
  }

  useEffect(() => {
    switch (type) {
      case 'poll':
        visitor.trackViewPoll(props.id, props.ctaId)
        break
      case 'message':
        visitor.trackViewMessage(props.id, props.ctaId)
        break
      default:
        break
    }
  }, [id, data])

  const formattedMessage = () => {
    switch (type) {
      case 'announcement':
        return (
          <>
            <MessageText>{ data.message }</MessageText>
            <AnnouncementImage uri={ data.image_url } padWidth={40} maxWidth={408} />
          </>
        )

      case 'normal':
      case 'jit_message':
      case 'real':
      case 'bad_message':
        return (
          <MessageText>{ data.message }</MessageText>
        )
      case 'poll':
        return (
          <Poll isPreview id={id} ctaId={ctaId} data={data} />
        )
      case 'file':
        return (
          <File isPreview data={data} />
        )
      case 'question':
      case 'jit_question':
        return (
          <Question data={{ ...data, answeredBy }} />
        )
      default:
        console.log('unknown type:', type)
        return null
    }
  }

  const renderContent = () => (
    <>
      {
        author.color
          ? <AttendeeAvatar color={author.color}>
            <AvatarInitial color={author.color}>{author.name.split(' ').slice(0, 2).map(name => name.charAt(0)).join('')}</AvatarInitial>
          </AttendeeAvatar>
          : <Avatar isAdmin={author.admin} avatarUrl={author.avatar_url} />
      }
      <Body>
        <Title>
          <Author>{ author.name }</Author>
          <SentAt>{ human_created_at }</SentAt>
        </Title>
        <Content>
          { formattedMessage() }
        </Content>
      </Body>
    </>
  )

  const renderIcon = () => {
    if (type === 'announcement') {
      return <Shout color={darkScheme ? '#ffffff' : '#131d40'} />
    }

    return null
  }

  const getWrapperTag = () => {
    switch (type) {
      case 'poll': return PollWrapper
      case 'jit_question':
      case 'question': return QuestionWrapper
      case 'file': return FileWrapper
      case 'announcement': return AnnouncementWrapper
      default: return BaseWrapper
    }
  }

  const WrapperTag = getWrapperTag()

  return (
    <View style={props.style}>
      <WrapperTag>
        {
          ['poll', 'question', 'jit_question', 'file', 'announcement'].includes(type)
          ? <StyledBlurView>{ renderContent() }</StyledBlurView>
          : renderContent()
        }
      </WrapperTag>
      <IconWrapper>
        { renderIcon() }
      </IconWrapper>
    </View>
  )
}
Message.propTypes = {
  className: PropTypes.string,
  isVertical: PropTypes.bool,
  id: PropTypes.number,
  author: PropTypes.object,
  data: PropTypes.object,
  type: PropTypes.string,
}

const StyledBlurView = styled(BlurView).attrs({ intensity: 40 })`
  flex: 1 1 auto;
  z-index: 1;
  flex-direction: row;
  padding: 9px;
  border-radius: 10px;
`

const BaseWrapper = styled.View`
  flex: 1 1 auto;
  flex-direction: row;
  padding: 9px;
`

const PollWrapper = styled.View`
  flex: 1 1 auto;
  border-radius: 10px;
  overflow: hidden;
  background-color: ${props => props.theme.darkScheme ? 'rgba(255, 128, 92, 0.16)' : 'rgba(255, 128, 92, 0.13)'};
`

const QuestionWrapper = styled(PollWrapper)`
  background-color: ${props => props.theme.darkScheme ? 'rgba(23, 203, 167, 0.15)' : '#f8f9fa'};
`

const AnnouncementWrapper = styled(PollWrapper)`
  background-color: rgba(231, 92, 255, 0.15);
`

const FileWrapper = styled(PollWrapper)`
  background-color: ${props => props.theme.darkScheme ? 'rgba(255, 255, 255, 0.05)' : '#f4f6f7'};
`

const Body = styled.View`
  flex: 1;
  justify-content: flex-start;
  margin-left: 10px;
`

const Title = styled.View`
  flex-direction: row;
  align-items: center;
`

const Author = styled.Text`
  font-size: 18px;
  font-family: Gilroy-SemiBold;
  color: ${props => props.theme.darkScheme ? '#ffffff' : '#131d40'};
`

const SentAt = styled.Text`
  margin-left: 9px;
  font-size: 14px;
  font-family: Gilroy-Regular;
  color: ${props => props.theme.darkScheme ? 'rgba(255, 255, 255, 0.3)' : '#9ea4b4'};
`

const Content = styled.View`
`

const MessageText = styled.Text`
  margin-top: 3px;
  font-size: 16px;
  line-height: 15px;
  font-family: Gilroy-Regular;
  color: ${props => props.theme.darkScheme ? 'rgba(255, 255, 255, 0.9)' : '#484d60'};
  ${() => mobile ? '' : 'white-space: pre-wrap;'}
`

const AnnouncementImage = styled(SmartImage)`
  border-radius: 4px;
`

const IconWrapper = styled.View`
  position: absolute;
  top: 10px;
  right: 10px;
`

const Shout = props => (
  <Svg width="22px" height="21px" viewBox="0 0 22 21">
    <G stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <G transform="translate(-67.000000, -681.000000)" fill={props.color} fillRule="nonzero">
        <G transform="translate(67.000000, 681.000000)">
          <Path d="M5.75003105,12.9329402 C5.98908174,12.7950504 6.0709136,12.4889979 5.93302981,12.249937 C5.79495068,12.0110715 5.48910644,11.9290408 5.25005576,12.0669305 C5.01100507,12.2050155 4.92897787,12.511068 5.067057,12.7499336 C5.20494078,12.9889945 5.51098036,13.0710252 5.75003105,12.9329402 L5.75003105,12.9329402 Z"></Path>
          <Path d="M4.09237203,16.359126 L6.23948858,20.1342449 C6.70282076,20.9497557 7.74506829,21.2516841 8.56930655,20.7680239 C9.38488444,20.2896044 9.66518205,19.2318408 9.19335603,18.4033129 L7.91427946,16.154234 L9.02197976,15.5050711 C9.22583259,15.3853817 9.29578209,15.120814 9.17803376,14.9138934 L8.62360071,13.9391348 C8.68971963,13.9261177 9.12440579,13.8395626 16.7900382,12.3131846 C17.7376874,12.2609473 18.3054442,11.2089315 17.8267898,10.3678936 L16.4098127,7.8767311 L17.3153257,6.48458106 C17.4042615,6.34764824 17.409591,6.17166426 17.328816,6.02965988 L16.4760982,4.53049936 C16.3953232,4.38849498 16.2419339,4.30532098 16.0812166,4.31495698 L14.4409009,4.41486722 L12.8678699,1.64916288 C12.6400344,1.24850764 12.2318291,1.00591686 11.776158,1.00016904 C11.770662,1 11.7653325,1 11.7598365,1 C11.3194878,1 10.9197763,1.22247353 10.6829473,1.59861607 L5.11330165,8.80060482 L1.49241572,10.922556 C0.0682772063,11.7568317 -0.425033293,13.6113413 0.400037691,15.0608003 C1.15382685,16.3861745 2.74301289,16.9075334 4.09237203,16.359126 L4.09237203,16.359126 Z M8.45505572,18.8362572 C8.69055235,19.2497604 8.551153,19.7788958 8.14261462,20.0184437 C7.73274387,20.2590059 7.21078735,20.1112537 6.97795542,19.7016386 L4.84616114,15.9532302 L6.32309491,15.0876797 C8.79031367,19.4259135 8.41541765,18.7667765 8.45505572,18.8362572 Z M7.4879206,15.4044847 L7.06156175,14.6549044 L7.80002859,14.2221292 L8.22638745,14.9717094 L7.4879206,15.4044847 Z M15.8693696,5.19487697 L16.4586109,6.23083273 L15.9331569,7.03856718 L14.9175567,5.2528621 L15.8693696,5.19487697 Z M11.3960991,2.07314738 C11.5105165,1.883132 11.6920521,1.86352187 11.7656656,1.8655505 C11.8384464,1.86639576 12.0198155,1.88938694 12.1292365,2.08176904 L17.0883229,10.8006689 C17.249873,11.0845086 17.0535147,11.4413792 16.7295819,11.4494937 C16.6714572,11.4508461 16.6446432,11.4614965 16.4957507,11.4897282 L11.2500379,2.26705095 C11.360125,2.12335606 11.3742815,2.10949375 11.3960991,2.07314738 L11.3960991,2.07314738 Z M10.6826142,3.00074029 L15.6112226,11.6658813 L8.17292605,13.1467841 L5.91122556,9.17049247 L10.6826142,3.00074029 Z M1.13850458,14.628025 C0.95130639,14.2992173 0.852377833,13.9259486 0.852377833,13.5482846 C0.852377833,12.7763894 1.26108275,12.0575767 1.91877458,11.6721363 L5.24187545,9.72464763 L7.37366973,13.472549 L4.05056886,15.4202066 C3.03247056,16.016625 1.72624691,15.6612759 1.13850458,14.628025 Z"></Path>
          <Path d="M4.93775665,13.3061266 C4.80958945,13.0134728 4.52570006,12.9130524 4.30380949,13.0820934 L3.49999998,13.6941798 C3.27847199,13.8629818 2.99403869,13.7623223 2.86605281,13.4701466 C2.73788561,13.1774928 2.45399622,13.0770724 2.23210565,13.2461134 C2.01021507,13.4151545 1.93407615,13.7895792 2.06224335,14.082233 C2.44420691,14.9549344 3.29623772,15.2626512 3.96408484,14.7543326 L4.76789435,14.1422462 C4.98978493,13.9732051 5.06592385,13.5990195 4.93775665,13.3061266 L4.93775665,13.3061266 Z"></Path>
          <Path d="M21.2149049,2.07439779 L18.2714735,3.89785972 C18.0166443,4.05590228 17.9248233,4.41402489 18.0667848,4.69772048 C18.2083336,4.98118632 18.5298104,5.08386806 18.7852586,4.9255958 L21.7284836,3.10213381 C21.9835191,2.94409125 22.0751338,2.58596864 21.9331723,2.3022731 C21.7914171,2.01834781 21.4697341,1.91635523 21.2149049,2.07439779 L21.2149049,2.07439779 Z"></Path>
          <Path d="M20.620576,6.70463252 L18.6441821,6.0226282 C18.3712315,5.92843435 18.0906879,6.1368961 18.0175547,6.48845001 C17.9444215,6.84000398 18.1064734,7.2010802 18.379424,7.29527405 L20.3560178,7.97727837 C20.6305669,8.07224431 20.9097118,7.8604369 20.9824453,7.51145656 C21.0555785,7.15990258 20.8935266,6.79856902 20.620576,6.70463252 Z"></Path>
          <Path d="M17.3525827,0.379241662 L17.0113103,2.35585315 C16.9642293,2.62880614 17.0684248,2.90915248 17.2441422,2.98248613 C17.4197309,3.05542013 17.6003364,2.89396622 17.647546,2.62081337 L17.9886898,0.644201935 C18.0357707,0.371248894 17.9315752,0.090702801 17.7558578,0.0175689514 C17.5802691,-0.0555648981 17.3996636,0.106288672 17.3525827,0.379241662 L17.3525827,0.379241662 Z"></Path>
        </G>
      </G>
    </G>
  </Svg>
)

const isEqual = (prev, next) => {
  switch (prev.type) {
    case 'real':
      return prev.data.message === next.data.message
    case 'question':
      return prev.data.answer === next.data.answer
    default:
      return true
  }
}
export default styled(React.memo(Message, isEqual))`
  margin-top: 15px;
  margin-left: 12px;
  margin-right: 12px;
`
